import { Container, OmegaComponent } from "@omegagrid/core";
import { ChatMessage } from "@reporting/rp-types/ai";
import { css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { cache } from 'lit/directives/cache.js';

@customElement('rp-mf-ai-chatcontent')
export class ChatContent extends OmegaComponent {

	static styles = [OmegaComponent.styles, css`
		:host {
			overflow: hidden;
		}

		og-container {
			height: 100%;
		}

		rp-mf-ai-chatmessage {
			margin: 0 20px 10px 0;

		}
	`];

	customStyle = css`
		.item {
			padding: 5px 0;
			line-height: normal;
		}

		.message {
			display: flex;
			flex-direction: row;
			padding: 5px;
			border: 1px solid var(--og-border-color);
			border-radius: var(--og-base-radius);
			user-select: text;
			white-space: pre-wrap;
		}

		.message-left {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			flex: 0 0 30px;
			margin-right: 10px;
			white-space: nowrap;
		}

		.message-avatar {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 25px;
			height: 25px;
			border-radius: 50%;
			position: relative;
			border: 1px solid var(--og-border-color);
		}

		.message-content {
			flex: 1;
			color: var(--og-text-color);
		}

		.spacer {
			height: 50px;
		}
	`;

	/** scrolled within this many pixels of the bottom counts as "at the bottom" */
	private static readonly STICK_THRESHOLD = 40;

	@property({type: Array})
	messages: ChatMessage[] = [];

	@query('og-container')
	container: Container;

	// Follow new content only when the view is already at the bottom, so the frequent polling
	// updates of an answer in progress don't yank the user away from an earlier message they
	// scrolled up to read. A message the user just sent always scrolls into view.
	private _stickToBottom = true;
	private _forceStick = false;

	add(message: ChatMessage) {
		this._forceStick = true;
		this.messages = [...this.messages, message];
	}

	willUpdate(props: Map<string, any>) {
		if (props.has('messages') && this.container) {
			const slider = this.container.verticalSlider;
			this._stickToBottom = this._forceStick || !slider || slider.value >= slider.maxValue - ChatContent.STICK_THRESHOLD;
			this._forceStick = false;
		}
	}

	updated(props: Map<string, any>) {
		if (props.has('messages') && this.container) {
			requestAnimationFrame(() => {
				this.container.updateSliders();
				if (this._stickToBottom) this.container.scrollVerticalTo(this.container.scrollHeight);
			});
		}
	}

	render = () => html`
		<og-container>
			<div slot="content">
				${this.messages.map((msg, i) => cache(html`
					<rp-mf-ai-chatmessage .message=${msg} .autoLoadView=${i === this.messages.length - 1}></rp-mf-ai-chatmessage>
				`))}
			</div>
		</og-container>
	`;

}
